import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  Psychology as PsychologyIcon,
  DataObject as DataObjectIcon,
  Speed as SpeedIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid2,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { styled } from '@mui/material/styles';

import PageHeader from '../../components/PageHeader';

// GraphQL Query
const GET_ML_MODEL_STATISTICS = gql`
  query GetMLModelStatistics {
    mlModelStatistics {
      modelName
      modelType
      latestVersion
      currentTrainingSamples
      currentTestSamples
      totalCurrentSamples
      currentR2
      currentAccuracy
      currentF1Score
      currentPrecision
      currentRecall
      currentMse
      currentRmse
      samplesChange1w
      samplesChange4w
      samplesChange1wPercent
      samplesChange4wPercent
      topFeatures {
        featureName
        importance
      }
      modelCreatedAt
      modelUpdatedAt
      totalVersions
    }
  }
`;

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 16,
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
    transform: 'translateY(-4px)',
  },
}));

const GradientCard = styled(Card)<{ gradient?: string }>(({ theme, gradient }) => ({
  height: '100%',
  borderRadius: 16,
  background:
    gradient ||
    `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.95)} 100%)`,
  color: '#FFFFFF',
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
    transform: 'translateY(-4px)',
  },
}));

const MetricBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: alpha(theme.palette.background.paper, 0.6),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const FeatureBar = styled(Box)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
  transition: 'all 0.3s ease',
}));

// Helper functions
const formatNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return 'N/A';
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  return num.toFixed(0);
};

const formatPercentage = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return 'N/A';
  const sign = num > 0 ? '+' : '';
  return `${sign}${num.toFixed(1)}%`;
};

const formatMetric = (num: number | null | undefined, decimals = 3): string => {
  if (num === null || num === undefined) return 'N/A';
  return num.toFixed(decimals);
};

// Change indicator component
const ChangeIndicator = ({ change, percentChange }: { change: number; percentChange: number }) => {
  const isPositive = change > 0;
  const isNeutral = change === 0;

  if (isNeutral) {
    return (
      <Chip
        size="small"
        label="No change"
        sx={{
          backgroundColor: alpha('#9E9E9E', 0.1),
          color: '#757575',
          fontWeight: 600,
        }}
      />
    );
  }

  return (
    <Chip
      size="small"
      icon={isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
      label={`${formatNumber(Math.abs(change))} (${formatPercentage(percentChange)})`}
      sx={{
        backgroundColor: alpha(isPositive ? '#4CAF50' : '#F44336', 0.1),
        color: isPositive ? '#2E7D32' : '#C62828',
        fontWeight: 600,
        '& .MuiChip-icon': {
          color: isPositive ? '#2E7D32' : '#C62828',
        },
      }}
    />
  );
};

// Model statistics card component
const ModelStatsCard = ({ model }: { model: any }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  // Determine primary metric based on model type
  const primaryMetric = model.modelType?.toLowerCase().includes('classification')
    ? { label: 'F1 Score', value: model.currentF1Score }
    : { label: 'R² Score', value: model.currentR2 };

  return (
    <StyledCard>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <PsychologyIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={700} color="primary.dark">
                {model.modelName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {model.modelType} • v{model.latestVersion}
              </Typography>
            </Box>
          </Box>
          <Chip label={`${model.totalVersions} versions`} size="small" variant="outlined" color="primary" />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Primary Metric - Large Display */}
        <Box textAlign="center" mb={3}>
          <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600}>
            {primaryMetric.label}
          </Typography>
          <Typography variant="h2" fontWeight={800} color="primary.main" sx={{ my: 1 }}>
            {formatMetric(primaryMetric.value, 4)}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(primaryMetric.value || 0) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              },
            }}
          />
        </Box>

        {/* Data Samples */}
        <GradientCard sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <DataObjectIcon sx={{ fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Training Data
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={700} mb={1}>
              {formatNumber(model.totalCurrentSamples)} samples
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Train: {formatNumber(model.currentTrainingSamples)}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                •
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Test: {formatNumber(model.currentTestSamples)}
              </Typography>
            </Box>
          </CardContent>
        </GradientCard>

        {/* Data Growth */}
        <Grid2 container spacing={1} mb={2}>
          <Grid2 size={6}>
            <MetricBox>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                1 Week Change
              </Typography>
              <ChangeIndicator change={model.samplesChange1w || 0} percentChange={model.samplesChange1wPercent || 0} />
            </MetricBox>
          </Grid2>
          <Grid2 size={6}>
            <MetricBox>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                4 Week Change
              </Typography>
              <ChangeIndicator change={model.samplesChange4w || 0} percentChange={model.samplesChange4wPercent || 0} />
            </MetricBox>
          </Grid2>
        </Grid2>

        {/* Performance Metrics */}
        <Grid2 container spacing={1} mb={2}>
          <Grid2 size={4}>
            <MetricBox>
              <Typography variant="caption" color="text.secondary">
                Accuracy
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {formatMetric(model.currentAccuracy, 3)}
              </Typography>
            </MetricBox>
          </Grid2>
          <Grid2 size={4}>
            <MetricBox>
              <Typography variant="caption" color="text.secondary">
                Precision
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {formatMetric(model.currentPrecision, 3)}
              </Typography>
            </MetricBox>
          </Grid2>
          <Grid2 size={4}>
            <MetricBox>
              <Typography variant="caption" color="text.secondary">
                Recall
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {formatMetric(model.currentRecall, 3)}
              </Typography>
            </MetricBox>
          </Grid2>
        </Grid2>

        {/* Top Features - Expandable */}
        {model.topFeatures && model.topFeatures.length > 0 && (
          <>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                Top Features
              </Typography>
              <IconButton
                size="small"
                onClick={handleExpandClick}
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s',
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1 }}>
                {model.topFeatures.slice(0, 5).map((feature: any, index: number) => (
                  <Box key={index} mb={1.5}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" fontWeight={500} noWrap sx={{ maxWidth: '70%' }}>
                        {feature.featureName}
                      </Typography>
                      <Typography variant="caption" fontWeight={700} color="primary.main">
                        {(feature.importance * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                    <FeatureBar sx={{ width: `${feature.importance * 100}%` }} />
                  </Box>
                ))}
              </Box>
            </Collapse>
          </>
        )}

        {/* Last Updated */}
        <Box mt={2} pt={2} borderTop={1} borderColor="divider">
          <Typography variant="caption" color="text.secondary">
            Last updated: {new Date(model.modelUpdatedAt).toLocaleDateString()} at{' '}
            {new Date(model.modelUpdatedAt).toLocaleTimeString()}
          </Typography>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

// Main component
const MLStatsPage = () => {
  const { loading, error, data, refetch } = useQuery(GET_ML_MODEL_STATISTICS, {
    pollInterval: 300000, // 5 minutes
    fetchPolicy: 'cache-and-network',
  });

  const handleRefresh = () => {
    refetch();
  };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <Typography variant="h6" color="error">
          Error loading ML model statistics
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          {error.message}
        </Typography>
        <Button variant="outlined" color="primary" onClick={() => refetch()} sx={{ mt: 2 }} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Box>
    );
  }

  const models = data?.mlModelStatistics || [];

  return (
    <Box>
      <PageHeader
        title="ML Model Statistics"
        subtitle="Monitor machine learning model performance, training data, and feature importance"
        variant="default"
      />

      <Box sx={{ p: 3 }}>
        {error && data && (
          <Paper
            sx={{
              p: 2,
              mb: 2,
              bgcolor: alpha('#FF564F', 0.1),
              borderLeft: '4px solid #FF564F',
            }}
          >
            <Typography variant="body2" color="error">
              Unable to fetch latest data. Showing cached results.
            </Typography>
          </Paper>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Active Models: {models.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Real-time performance tracking and data insights
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            Refresh
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {models.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh">
            <SpeedIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No ML models found
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              ML model statistics will appear here once models are trained
            </Typography>
          </Box>
        ) : (
          <Grid2 container spacing={3}>
            {models.map((model: any) => (
              <Grid2 size={{ xs: 12, md: 6, lg: 4 }} key={model.modelName}>
                <ModelStatsCard model={model} />
              </Grid2>
            ))}
          </Grid2>
        )}
      </Box>
    </Box>
  );
};

export default MLStatsPage;
